import { cn } from '../../lib/utils';
import { panelCount } from './panel-format';

/**
 * What the catalogue can do for this product, said once above the tiers.
 *
 * Three states, because they call for three different things: nothing to offer,
 * suggestions waiting to be looked at, and suggestions already dealt with. Only
 * the middle one is loud, and only it carries an action.
 */
export function CatalogCoverageBanner({
	priceCount,
	lowestQuantity,
	highestQuantity,
	pendingCount,
	tierCount,
	hasSupplier,
	onApplyAll,
}: {
	/** Distinct catalogue breaks available for this product. */
	priceCount: number;
	lowestQuantity: number;
	highestQuantity: number;
	/** Tiers carrying a suggestion the admin has not acted on. */
	pendingCount: number;
	tierCount: number;
	hasSupplier: boolean;
	onApplyAll: () => void;
}) {
	/*
	 * Said in EVERY state, not just the quiet one.
	 *
	 * Without a supplier the lookup returns the no-supplier ladder only, so a
	 * supplier's own prices are invisible — and there is no way to tell that
	 * from a product that simply has none. That silence reads as missing data.
	 */
	const supplierHint = hasSupplier ? null : ' · assign a supplier to see its own prices';

	if (priceCount === 0) {
		return (
			<Banner quiet>
				No catalogue prices for this product{supplierHint ?? ''} — otherwise enter cost and
				sale price manually.
			</Banner>
		);
	}

	const range =
		lowestQuantity === highestQuantity
			? `${panelCount(lowestQuantity)} pcs`
			: `${panelCount(lowestQuantity)}–${panelCount(highestQuantity)} pcs`;

	if (pendingCount > 0) {
		return (
			<Banner>
				<span>
					<b className="font-medium">
						{priceCount} catalogue {priceCount === 1 ? 'price' : 'prices'}
					</b>{' '}
					({range}) match {pendingCount} of {tierCount}{' '}
					{tierCount === 1 ? 'quantity' : 'quantities'}. Review the suggestions or apply
					them all.
					{supplierHint}
				</span>
				<button
					type="button"
					onClick={onApplyAll}
					className="ml-auto shrink-0 rounded-[var(--r-2)] border border-accent bg-accent px-2 py-0.5 text-2xs text-accent-fg transition-colors hover:bg-accent-hover"
				>
					Use all suggestions
				</button>
			</Banner>
		);
	}

	return (
		<Banner quiet>
			<span>
				{priceCount} catalogue {priceCount === 1 ? 'price' : 'prices'} · {range}
				{supplierHint}
			</span>
		</Banner>
	);
}

function Banner({ children, quiet }: { children: React.ReactNode; quiet?: boolean }) {
	return (
		<div
			className={cn(
				'flex items-center gap-2.5 rounded-[var(--r-3)] border px-2.5 py-1.5 text-xs',
				quiet
					? 'border-line-1 bg-surface-fill text-text-2'
					: 'border-accent-line bg-accent-soft text-text-1'
			)}
		>
			{children}
		</div>
	);
}
